import React from 'react';
import { Calendar, CheckCircle, Clock, PlayCircle } from 'lucide-react';

interface Phase {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  order: number;
  status?: string;
  deliverables?: Array<{
    name: string;
    description: string;
    required: boolean;
  }>;
}

interface TimelineProps {
  phases: Phase[];
  currentDate?: Date;
  className?: string;
}

const Timeline: React.FC<TimelineProps> = ({ 
  phases, 
  currentDate = new Date(),
  className = "" 
}) => {
  const sortedPhases = [...phases].sort((a, b) => a.order - b.order);

  const getPhaseStatus = (phase: Phase) => {
    const now = currentDate;
    const startDate = new Date(phase.startDate);
    const endDate = new Date(phase.endDate);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'active';
    return 'completed';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'active':
        return <PlayCircle className="h-5 w-5 text-blue-500" />;
      case 'upcoming':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50';
      case 'active':
        return 'border-blue-500 bg-blue-50';
      case 'upcoming':
        return 'border-gray-300 bg-gray-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Hackathon Timeline</h3>
      </div>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        <div className="space-y-6">
          {sortedPhases.map((phase, index) => {
            const status = getPhaseStatus(phase);
            const isLast = index === sortedPhases.length - 1;
            
            return (
              <div key={phase._id} className="relative flex items-start">
                {/* Phase indicator */}
                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${getStatusColor(status)}`}>
                  {getStatusIcon(status)}
                </div>
                
                {/* Phase content */}
                <div className="ml-6 flex-1">
                  <div className={`p-4 rounded-lg border ${getStatusColor(status)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {phase.name}
                      </h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        status === 'completed' ? 'bg-green-100 text-green-800' :
                        status === 'active' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3">
                      {phase.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(phase.startDate)} at {formatTime(phase.startDate)}</span>
                      </div>
                      <span>to</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(phase.endDate)} at {formatTime(phase.endDate)}</span>
                      </div>
                    </div>
                    
                    {/* Deliverables */}
                    {phase.deliverables && phase.deliverables.length > 0 && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium text-gray-900 mb-2">Deliverables:</h5>
                        <ul className="space-y-1">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <span className={`w-2 h-2 rounded-full ${
                                deliverable.required ? 'bg-red-500' : 'bg-blue-500'
                              }`}></span>
                              <span className={deliverable.required ? 'font-medium' : ''}>
                                {deliverable.name}
                              </span>
                              {deliverable.required && (
                                <span className="text-xs text-red-600">(Required)</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline; 